/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path pages
 * @description Login component
 *
 * @section 4.1.2
 *
 */
import Button from "@material-ui/core/Button";
import Container from "@material-ui/core/Container";
import InputAdornment from "@material-ui/core/InputAdornment";
import SvgIcon from "@material-ui/core/SvgIcon";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import UserIcon from "@material-ui/icons/AccountCircle";
import React, { useEffect, useState } from "react";
interface LoginFormProps {
  /** Function for handling form submit */
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  /**  CSS classes for this component */
  classes: {
    paper: string;
    form: string;
    submit: string;
    logo: string;
    inputRoot: string;
    focused: string;
    error: string;
    outline: string;
  };
  /** boolean, set true if previous login was failed and false if login is ok. Only use to indicate invalid login (not authentication per se) */
  errorMessage?: string;
}
type Translations = {
  login: string;
  password: string;
  signin: string;
  username: string;
};
const selectedLang = localStorage.getItem("i18nextLng") || "en";
const LoginForm = ({
  handleSubmit,
  classes,
  errorMessage = "",
}: LoginFormProps) => {
  const [formtranslations, setFormTranslations] = useState<Translations | null>(
    null
  );
  useEffect(() => {
    const fetchTranslations = async () => {
      try {
        const response = await fetch(`${process.env.PUBLIC_URL}/locale.json`);
        const data = await response.json();

        setFormTranslations(data[selectedLang]);
      } catch (error) {
        console.error("Error loading the login screen translations:", error);
      }
    };
    fetchTranslations();
  }, []);
  return (
    <Container
      dir={selectedLang === "ar" ? "rtl" : "ltr"}
      component="main"
      maxWidth="xs"
      data-testid="loginform-main"
    >
      <div className={classes.paper}>
        <div className={classes.logo} />
        <Typography
          color="primary"
          component="h1"
          variant="h6"
          data-testid="login-prompt"
        >
          {formtranslations?.login || "Log in to Your Account"}
        </Typography>
        <form
          className={classes.form}
          noValidate
          onSubmit={handleSubmit}
          data-testid="form"
        >
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="email"
            name="email"
            autoComplete="email"
            autoFocus
            placeholder={formtranslations?.username || "Username"}
            data-testid="email"
            InputProps={{
              classes: {
                root: classes.inputRoot,
                focused: classes.focused,
                error: classes.error,
                notchedOutline: classes.outline,
              },
              startAdornment: (
                <InputAdornment position="start">
                  <UserIcon />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            error={errorMessage !== ""}
            name="password"
            placeholder={formtranslations?.password || "Password"}
            type="password"
            id="password"
            data-testid="password"
            autoComplete="current-password"
            InputProps={{
              classes: {
                root: classes.inputRoot,
                focused: classes.focused,
                error: classes.error,
                notchedOutline: classes.outline,
              },
              startAdornment: (
                <InputAdornment position="start">
                  <SvgIcon viewBox="0 0 960 960">
                    <path d="M384 886.4v-419.2c-32-9.6-60.8-32-92.8-60.8-92.8-92.8-92.8-243.2 0-336s246.4-92.8 339.2 0 92.8 243.2 0 336c-22.4 22.4-54.4 41.6-86.4 54.4v128l41.6 41.6-41.6 41.6 41.6 41.6-41.6 41.6v6.4l44.8 38.4-41.6 41.6-3.2 38.4-83.2 80-76.8-73.6zM502.4 96c-25.6-25.6-64-25.6-89.6 0s-25.6 64 0 89.6c25.6 25.6 64 25.6 89.6 0s22.4-64 0-89.6z" />
                  </SvgIcon>
                </InputAdornment>
              ),
            }}
            helperText={
              <Typography
                component="span"
                variant="subtitle2"
                data-testid="error-message"
              >
                {errorMessage}
              </Typography>
            }
            FormHelperTextProps={{
              error: true,
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            data-testid="sign-in"
            className={classes.submit}
          >
            {formtranslations?.signin || "Sign In"}
          </Button>
        </form>
      </div>
    </Container>
  );
};
export default LoginForm;
